import React from 'react';
import { Messages, Control, Code, Name, Title, Actions, Form, Files, Toast, Button, API, Utility, useListContext, useCallback, useEffect, useState, Hooks } from 'helpers/ListImports';
import { PurchaseReturn } from 'models/PurchaseReturnModel';
import { ControlSession } from 'components/common/ControlSession';

const PurchaseReturnList: React.FC = () => {
  const width = { sno: 10, supplier:36, amount: 12, segment:12,  date: 12, createdby: 12 }
  const [formId, formName] = [Code.Form.Id.PURCHASERETURN, Code.Form.Name.PURCHASERETURN];
  const { loading, setLoading, error, setError, search, setSearch, pno, setPno, pageRows, setPageRow, activeId, setActiveId, selected, setSelected, confirm, setConfirm, message, setMessage,  showMenu, setShowMenu, selectedItems, setSelectedItems, selectAllChecked, setSelectAllChecked, activeOptions, setActiveOptions, rowOptions, setRowOptions, menuPosition, setMenuPosition, iframeRef, filterRef, contextMenuRef  } = useListContext();
  const [purchaseReturnData, setPurchaseReturnData] = useState<PurchaseReturn[]>([]); 
  const [selectedPurchaseReturn, setselectedPurchaseReturn] = useState<PurchaseReturn | null>(null);
  const [userId, setUserId] = useState<number>(1);
  const [userOptions, setUserOptions] = Hooks.Form.Options();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const triggers = [pno, pageRows, search, activeId, userId, startDate, endDate];
  const Privilege = Hooks.Privilege.List(formId);
  const locationId = Utility.Storage.LocationId();
  
  Hooks.Privilege.HasView(Privilege);

  Form.Click.Outside([contextMenuRef, filterRef],[() => setShowMenu(false),() => {}],true);
  Form.Select.OnChange(selectedItems, setSelected);

  useEffect(() => {
    Files.Json.API(Code.File.USER, Code.Option.Common.ALL, setUserOptions, setError);
    Files.Json.Http(Code.File.ACTIVE, Code.Option.Common.NOTHING, setActiveOptions, setError);
    Files.Json.Http(Code.File.ROW, Code.Option.Common.NOTHING, setRowOptions, setError);
  }, []);

  useEffect(() => {
    callListData(); 
  }, triggers);
 
  const callListData = useCallback(async () => {
    const url = API.Build.ListApiEndpoint(formName, pno, pageRows, Name.PurchaseReturn.Id, Code.Option.Sort.DESC, activeId, search, {
      fields: Utility.Join.Fields([
        Name.PurchaseReturn.Number, 
        Name.PurchaseReturn.Date, 
        Name.Supplier.Name, 
        Name.PurchaseReturn.Net,
        Name.Common.SegmentName,
        Name.User.FullName,
      ]),
      ...(!Privilege.ALLLOC && { location_id: locationId }),
      ...(userId != 1 && { user_id: userId }),
      ...(startDate != '' && { start_date: startDate }),
      ...(endDate != '' && { end_date: endDate }),
    });
    await API.Data.List<PurchaseReturn>(url, setPurchaseReturnData, setError, setLoading); 
 }, triggers);

  return (
    <Control.List.Frame>
      <Control.List.Panel>
        <Control.List.Tools>
          <Control.Tool.Left>
            <Button.Create title={formName} onClick={() => Form.Show.Popup(iframeRef, formName)} Privilege={Privilege} />
            {selected && ( <Button.Delete onClick={()=>Toast.Delete.MultipleRecords(activeId, selectedItems, setConfirm)} activeId={activeId}  Privilege={Privilege} /> )}
          </Control.Tool.Left>
          <Control.Tool.Right>
            <Control.SearchBar onSearch={setSearch} onFilterToggle={() => Form.Show.Filter(filterRef)}  onGoClick={() => callListData()} />
            <Control.List.Filter filterRef={filterRef}>
              <ControlSession startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
              <Control.Dropdown title={Title.Created} value={userId} options={userOptions} onChange={setUserId} />
              <Control.Dropdown title={Title.Status} value={activeId} options={activeOptions} onChange={setActiveId} />
              <Control.Dropdown title={Title.Rows} value={pageRows} options={rowOptions} onChange={setPageRow} />
            </Control.List.Filter>
            <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.MultiDelete(formName, activeId, callListData, selectedItems, setConfirm, setMessage, setSelected, setSelectedItems, setError) } onRefuse={() => setSelectedItems([])} />
          </Control.Tool.Right>
        </Control.List.Tools>
        <Control.List.Data>
          <Control.List.Box>
            <Control.List.Title>
              <Control.SelectAll selectAllChecked={selectAllChecked} onSelectAll={(e) => Form.Select.All(e, setSelectedItems, purchaseReturnData, setSelectAllChecked,formName)} extra={2} />
              <Control.Grid.Items values={[
                [Title.RefSno, width.sno], 
                [Title.Supplier, width.supplier], 
                [Title.Amount, width.amount, Code.Align.RIGHT],
                [Title.Segment, width.segment],
                [Title.Date, width.date],
                [Title.CreatedBy, width.createdby]
              ]} last={1} />
            </Control.List.Title>
            {!loading && !error && Array.isArray(purchaseReturnData) && (
              <Control.Grid.Row>
                {purchaseReturnData.map((purchaseReturnData) => { return (
                  <Control.Grid.Data<PurchaseReturn> key={purchaseReturnData.purchasereturn_id} itemKey={purchaseReturnData.purchasereturn_key} data={purchaseReturnData} setMenuPosition={setMenuPosition} setSelectedData={setselectedPurchaseReturn} setShowMenu={setShowMenu}>
                    <Control.Grid.Check keyId={purchaseReturnData.purchasereturn_key} selectedItems={selectedItems} toggleSelection={Form.Select.Toggle} setSelectedItems={setSelectedItems} />
                    <Control.Grid.Modify path={Utility.Join.Edit(Privilege, formName, purchaseReturnData.purchasereturn_key)} iframeRef={iframeRef} Privilege={Privilege} />
                    <Control.Grid.Print path={Utility.Join.Print(Privilege, formName, purchaseReturnData.purchasereturn_key)} iframeRef={iframeRef} Privilege={Privilege} />
                    <Control.Grid.Text value={purchaseReturnData.purchasereturn_number} width={width.sno} />
                    <Control.Grid.Items values={[
                      [purchaseReturnData.supplier_name, width.supplier], 
                      [Utility.Format.Amount(purchaseReturnData.purchasereturn_net_amount), width.amount, Code.Align.RIGHT],
                      [purchaseReturnData.segment_name, width.segment], 
                      [Utility.Format.DateOnly(purchaseReturnData.purchasereturn_date), width.date], 
                      [Utility.Get.FullName(purchaseReturnData,Code.Form.Name.USER), width.createdby], 
                    ]} /> 
                    <Control.Grid.Actions>
                      <Control.Action.All formName={formName} activeId={activeId} keyId={purchaseReturnData.purchasereturn_key} iframeRef={iframeRef} setSelectedItems={setSelectedItems} setConfirm={setConfirm} Privilege={Privilege}><></></Control.Action.All>
                    </Control.Grid.Actions>
                  </Control.Grid.Data>)})}
              </Control.Grid.Row>
            )}
          </Control.List.Box>
        </Control.List.Data>
        <Control.List.Info>
          <Control.RecordInfo pageNo={pno} pageRows={pageRows} loading={loading} error={error} dataLength={purchaseReturnData.length} />
          <Control.Pagination pageNo={pno} onChange={(page) => setPno(page)} />
        </Control.List.Info>
      </Control.List.Panel>
      {showMenu && (<div ref={contextMenuRef}><Control.ListContextMenu showMenu={showMenu} formName={formName} onCreate={() => Form.Show.Popup(iframeRef, formName)} onModify={()=>Form.Show.Popup(iframeRef, `${formName}/${selectedPurchaseReturn?.purchasereturn_key}`)} onDelete={() => Toast.Delete.SingleRecord(activeId, selectedPurchaseReturn?.purchasereturn_key, setSelectedItems, setConfirm)} onClose={() => Form.Parent.TabClose()} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Control.FormPopup iframeRef={iframeRef} onRefresh={callListData} />
      <Actions.List />
    </Control.List.Frame>
  );
};

export default PurchaseReturnList;